/**
 * Human labels for due dates and times, in the style of ClickUp's list rows:
 * "Today", "Tomorrow", "Yesterday", "Sat", "6 days ago", "9/20/26".
 */
import {
  dayOfWeek,
  diffDays,
  localDateTimeToInstant,
  parseISODate,
  parseTime,
  todayInTz,
} from './time.ts';
import type { ISODate, ISOTime, Instant } from './types.ts';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;
const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

/** `17:00` → `5pm`, `09:30` → `9:30am`. */
export function formatTime12(time: ISOTime): string {
  const { hour, minute } = parseTime(time);
  const suffix = hour < 12 ? 'am' : 'pm';
  const h = hour % 12 === 0 ? 12 : hour % 12;
  return minute === 0 ? `${h}${suffix}` : `${h}:${String(minute).padStart(2, '0')}${suffix}`;
}

/** `2026-09-20` → `9/20/26`. */
export function formatShortDate(date: ISODate): string {
  const { year, month, day } = parseISODate(date);
  return `${month}/${day}/${String(year).slice(-2)}`;
}

/** `2026-09-20` → `Sep 20`. */
export function formatMonthDay(date: ISODate): string {
  const { month, day } = parseISODate(date);
  return `${MONTHS[month - 1]} ${day}`;
}

export function weekdayShort(date: ISODate): string {
  return WEEKDAYS[dayOfWeek(date)]!;
}

export interface DueLabelOptions {
  /** `relative` uses "6 days ago" for recent past dates (list view); `short` uses 9/20/26. */
  pastStyle?: 'relative' | 'short';
}

/** Label for a calendar date relative to `today`. */
export function formatRelativeDate(
  date: ISODate,
  today: ISODate,
  opts: DueLabelOptions = {},
): string {
  const days = diffDays(date, today);
  if (days === 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  if (days === -1) return 'Yesterday';
  if (days > 1 && days < 7) return weekdayShort(date);
  if (days < -1 && days > -7 && opts.pastStyle === 'relative') return `${-days} days ago`;
  return formatShortDate(date);
}

export function formatDueLabel(
  dueDate: ISODate | null,
  dueTime: ISOTime | null,
  today: ISODate,
  opts: DueLabelOptions = {},
): string {
  if (!dueDate) return '';
  const day = formatRelativeDate(dueDate, today, opts);
  return dueTime ? `${day}, ${formatTime12(dueTime)}` : day;
}

export type DueTone = 'none' | 'overdue' | 'today' | 'future' | 'done';

/** Colour bucket for a due chip: red overdue, orange today, grey future. */
export function dueTone(
  task: { due_date: ISODate | null; due_time: ISOTime | null; due_at?: Instant | null },
  now: Date,
  tz: string,
  complete = false,
): DueTone {
  if (!task.due_date) return 'none';
  if (complete) return 'done';
  const today = todayInTz(tz, now);
  if (task.due_time) {
    const at = task.due_at
      ? Date.parse(task.due_at)
      : localDateTimeToInstant(task.due_date, task.due_time, tz).getTime();
    if (at < now.getTime()) return 'overdue';
  } else if (task.due_date < today) {
    return 'overdue';
  }
  return task.due_date === today ? 'today' : 'future';
}

/** "Good morning" / "Good afternoon" / "Good evening" for the user's local hour. */
export function greeting(localHour: number): string {
  if (localHour < 12) return 'Good morning';
  if (localHour < 17) return 'Good afternoon';
  return 'Good evening';
}

/** "just now", "5m ago", "3h ago", "2d ago". */
export function formatAgo(instant: Instant, now: Date): string {
  const s = Math.max(0, Math.round((now.getTime() - Date.parse(instant)) / 1000));
  if (s < 60) return 'just now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d < 30) return `${d}d ago`;
  const mo = Math.round(d / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.round(mo / 12)}y ago`;
}
